import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useSOSStore } from '@/stores/sos'
import { useUIStore } from '@/stores/ui'
import { USER_ROLES } from '@/constants/roles'
import { ROUTES } from '@/constants/routes'
import { prefetchHandlers } from '@/lib/routePrefetch'

interface NavItem {
  path: string
  icon: string
  label: string
  variant?: 'sos' | 'location' | 'community' | 'settings'
}

// 5 items evenly spaced on a 120° arc (–150° to –30°), radius 130
const RADIAL_POSITIONS = [
  { x: -134, y: -78 },
  { x: -78,  y: -134 },
  { x: 0,    y: -155 },
  { x: 78,   y: -134 },
  { x: 134,  y: -78 },
]

function itemBtnClass(item: NavItem, isActive: boolean): string {
  const base = 'relative w-12 h-12 rounded-full shadow-2xl ring-4 flex items-center justify-center transition-all active:scale-95'
  if (isActive) {
    if (item.variant === 'sos')      return `${base} bg-danger-600 ring-white`
    if (item.variant === 'location') return `${base} bg-success-600 ring-success-300`
    if (item.variant === 'settings') return `${base} bg-settings-500 ring-settings-300`
    if (item.variant === 'community')return `${base} bg-community-600 ring-community-300`
    return `${base} bg-beacon-600 ring-beacon-300`
  }
  if (item.variant === 'sos') return `${base} bg-danger-500 ring-white`
  return `${base} bg-beacon-500 ring-beacon-200`
}

export function FAB() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024)
  const navigate = useNavigate()
  const location = useLocation()
  const primaryRole = useAuthStore((s) => s.primaryRole())
  const isSOSActive = useSOSStore((s) => s.isSOSActive)
  const sidebarExpanded = useUIStore((s) => s.sidebarExpanded)

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  if (sidebarExpanded && isDesktop) return null

  const allItems: NavItem[] = (() => {
    if (
      primaryRole === USER_ROLES.FAMILY_ADMIN ||
      primaryRole === USER_ROLES.CO_CARER ||
      primaryRole === USER_ROLES.SUPER_ADMIN
    ) {
      return [
        { path: ROUTES.HOME,             icon: 'fa-house',                label: 'Home' },
        { path: ROUTES.MISSION_CONTROL,  icon: 'fa-tower-broadcast',      label: 'Mission' },
        { path: ROUTES.SOS,              icon: 'fa-triangle-exclamation', label: 'SOS',      variant: 'sos' },
        { path: ROUTES.LOCATION,         icon: 'fa-location-dot',         label: 'Location', variant: 'location' },
        { path: ROUTES.SETTINGS,         icon: 'fa-gear',                 label: 'Settings', variant: 'settings' },
      ]
    }
    if (primaryRole === USER_ROLES.ORG_ADMIN || primaryRole === USER_ROLES.STAFF) {
      // Stay on family shell — do not deep-link /org/* (that used to auto-open Lumen).
      return [
        { path: ROUTES.HOME,      icon: 'fa-house',        label: 'Home' },
        { path: ROUTES.ORG,       icon: 'fa-building',     label: 'Org' },
        { path: ROUTES.COMMUNITY, icon: 'fa-people-group', label: 'Community', variant: 'community' },
        { path: ROUTES.SETTINGS,  icon: 'fa-gear',         label: 'Settings',  variant: 'settings' },
      ]
    }
    if (primaryRole === USER_ROLES.FUNDER) {
      return [
        { path: ROUTES.HOME,      icon: 'fa-house',        label: 'Home' },
        { path: ROUTES.FIND_HELP, icon: 'fa-hand-holding-heart', label: 'Find Help' },
        { path: ROUTES.COMMUNITY, icon: 'fa-people-group', label: 'Community', variant: 'community' },
      ]
    }
    return [
      { path: ROUTES.HOME,      icon: 'fa-house',               label: 'Home' },
      { path: ROUTES.SOS,       icon: 'fa-triangle-exclamation', label: 'SOS', variant: 'sos' },
      { path: ROUTES.COMMUNITY, icon: 'fa-people-group',        label: 'Community' },
    ]
  })()

  function handleNavigate(path: string) {
    navigate(path)
    setIsOpen(false)
  }

  const fabClass = [
    'relative w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95 ring-2',
    isOpen
      ? 'bg-beacon-700 ring-beacon-400'
      : isSOSActive
        ? 'bg-danger-600 ring-danger-300 animate-pulse'
        : 'bg-beacon-600 ring-beacon-200',
  ].join(' ')

  return (
    <div className="fixed bottom-6 inset-x-0 z-40 flex items-end justify-center pointer-events-none">
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB + radial items */}
      <div className="relative pointer-events-auto flex flex-col items-center gap-1">
        {/* Radial items */}
        {isOpen && allItems.map((item, i) => {
          const pos = RADIAL_POSITIONS[i] ?? { x: 0, y: -90 }
          const isActive = item.path === location.pathname
          return (
            <div
              key={item.path}
              className="group absolute flex flex-col-reverse items-center gap-2 transition-all duration-200"
              style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, transitionDelay: `${i * 25}ms` }}
            >
              <button
                {...prefetchHandlers(item.path)}
                onClick={() => handleNavigate(item.path)}
                className={itemBtnClass(item, isActive)}
              >
                <i className={`fa-solid ${item.icon} text-base text-white`} />
                {item.variant === 'sos' && isSOSActive && (
                  <span className="absolute -top-0.5 -right-0.5 flex">
                    <span className="animate-ping absolute w-3 h-3 rounded-full bg-danger-400 opacity-75" />
                    <span className="w-3 h-3 rounded-full bg-danger-500 ring-2 ring-white" />
                  </span>
                )}
              </button>
              <span className="text-[10px] font-semibold text-white drop-shadow leading-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {item.label}
              </span>
            </div>
          )
        })}

        {/* FAB button */}
        <button onClick={() => setIsOpen((o) => !o)} className={fabClass}>
          {isOpen ? (
            <span className="relative w-4 h-4 flex items-center justify-center">
              <span className="absolute w-4 h-0.5 bg-white rounded-full rotate-45" />
              <span className="absolute w-4 h-0.5 bg-white rounded-full -rotate-45" />
            </span>
          ) : (
            <i className="fa-solid fa-shield-heart text-lg text-white" />
          )}
          {isSOSActive && !isOpen && (
            <span className="absolute -top-0.5 -right-0.5 flex">
              <span className="animate-ping absolute w-3.5 h-3.5 rounded-full bg-danger-400 opacity-75" />
              <span className="w-3.5 h-3.5 rounded-full bg-danger-500 ring-2 ring-white" />
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
